/* eslint-disable no-redeclare */
/* global alert */

import autobind from "autobind-decorator";
import delegate from "delegate";
import { Modal } from "bootstrap.native";
import { lazyValidate, ValidationError } from "../lazyValidate";
import existingAutoReordersHtml from "./existingAutoReorders.html";
import nextAutoReorderBannerHtml from "./nextAutoReorderBanner.html";
import Notification from "../Notification";

export default class AutoReorder {
  static get nextAutoReorderBanner() {
    return document.querySelector("[data-component=nextAutoReorderBanner]");
  }

  get eligable() {
    return this.product.itemNumber.autoReorderEligible;
  }

  get autoReorderContainer() {
    return this.container.querySelector("[data-component=AutoReorder]");
  }

  get onOffSwitch() {
    return this.autoReorderContainer.querySelector(
      "[data-component=onOffSwitch]",
    );
  }

  get onOffSwitchInput() {
    return this.onOffSwitch.querySelector("input");
  }

  get intervalDropdown() {
    return this.autoReorderContainer.querySelector(
      "[data-component=intervalDropdown]",
    );
  }

  get autoReorderOptionsContainer() {
    return this.autoReorderContainer.querySelector(
      "[data-component=autoReorderOptionsContainer]",
    );
  }

  get newAutoReorderRadio() {
    return this.autoReorderContainer.querySelector(
      "[data-component=newAutoReorderRadio]",
    );
  }

  get buyButton() {
    return this.container.querySelector("[data-action=addToCart]");
  }

  get new() {
    return this.newAutoReorderRadio.checked;
  }

  get existing() {
    return (
      this.existingAutoReorderRadio && this.existingAutoReorderRadio.checked
    );
  }

  constructor(container, product) {
    this.product = product;
    this.container = container;
    if (!container || !this.autoReorderContainer) return;

    this.intervalDropdown.value = "0";

    this.container.addEventListener("suffixChange", this.suffixChange);
    this.onOffSwitch.addEventListener("click", this.toggle);
    this.newAutoReorderRadio.addEventListener("click", this.newAutoReorder);
    delegate(
      this.container,
      "[data-component=existingAutoReorderRadio]",
      "click",
      this.existingAutoReorderRadioAction,
      false,
    );

    this.populateIntervalDropdown();
    this.populateAutoReordersDropdown();

    if (!this.product.unselectedSuffixItem) {
      this.populateNextAutoReorderBanner();
    }

    const ccModalElement = document.getElementById(
      "commonCarrierShippingOptions",
    );
    ccModalElement.addEventListener("hidden.bs.modal", () => {
      ccModalElement.style = "display: none;";
    });
    this.commonCarrierShippingOptions = new Modal(ccModalElement);
    this.addEventListenerToCommonCarrierSettingsModalSaveButton();
  }

  static renderNextAutoReorderBanner(autoReorders) {
    if (!autoReorders.length) {
      AutoReorder.nextAutoReorderBanner.innerHTML = "";
      return;
    }

    AutoReorder.nextAutoReorderBanner.innerHTML = nextAutoReorderBannerHtml(
      autoReorders[0],
    );
  }

  @lazyValidate
  interval() {
    if (!this.enabled) return;
    if (this.new && this.intervalDropdown.value === "0") {
      this.intervalDropdown.focus();
      throw new ValidationError("Please select Auto Reorder interval");
    }
    if (this.existing && this.existingDropdown.value === "0") {
      this.existingDropdown.focus();
      throw new ValidationError("Please select an existing Auto Reorder");
    }
  }

  @autobind
  toggle() {
    this.enabled = !this.enabled;
    this.autoReorderOptionsContainer.hidden =
      !this.autoReorderOptionsContainer.hidden;
    this.autoReorderContainer.classList.toggle("toggle--active");
    this.onOffSwitchInput.checked = !this.onOffSwitchInput.checked;

    this.newAutoReorder();
  }

  @autobind
  newAutoReorder() {
    if (this.existingDropdown) this.existingDropdown.disabled = true;

    this.newAutoReorderRadio.checked = true;
    this.intervalDropdown.disabled = false;
    this.buyButton.value = "Add to Cart";

    this.dispatchAutoReorderChange();
  }

  dispatchAutoReorderChange() {
    this.container.dispatchEvent(
      new Event("autoReorderChange", { bubbles: true }),
    );
  }

  @autobind
  existingAutoReorderRadioAction() {
    this.intervalDropdown.disabled = true;
    this.existingDropdown.disabled = false;
    this.buyButton.value = "Add to Auto Reorder";

    this.dispatchAutoReorderChange();
  }

  @autobind
  suffixChange() {
    this.populateNextAutoReorderBanner();

    if (this.eligable) {
      this.autoReorderContainer.hidden = false;

      if (this.existing) this.existingAutoReorderRadioAction();
    } else {
      this.autoReorderContainer.hidden = true;
    }
  }

  renderExistingAutoReordersDropdown(results) {
    if (!results[0].userHasAutoReorders) return;

    const containerDiv = this.autoReorderOptionsContainer.appendChild(
      document.createElement("div"),
    );

    containerDiv.innerHTML = existingAutoReordersHtml(results);
    this.existingDropdown = this.autoReorderContainer.querySelector(
      "[data-component=existingDropdown]",
    );
    this.existingAutoReorderRadio = this.autoReorderContainer.querySelector(
      "[data-component=existingAutoReorderRadio]",
    );
  }

  async populateAutoReordersDropdown() {
    const response = await fetch(
      "/myaccount:autoreorder/getautoreorderidsandnames/",
      {
        credentials: "include",
        headers: {
          accept: "application/json",
        },
      },
    );
    const json = await response.json();
    this.renderExistingAutoReordersDropdown(json);
  }

  async populateIntervalDropdown() {
    const intervalRequest = await fetch(
      "/myaccount:autoreorder/getintervallist/",
      {
        credentials: "include",
        headers: {
          accept: "application/json",
        },
      },
    );
    const intervalList = await intervalRequest.json();

    Object.values(intervalList)
      .sort((a, b) => a.order_by > b.order_by)
      .forEach((interval) => {
        const option = document.createElement("option");

        option.value = interval.id;
        option.textContent = `Ship every ${interval.interval_description}`;

        this.intervalDropdown.options.add(option);
      });
  }

  existingFormData() {
    const existingFormData = new FormData();

    existingFormData.append("autoReorderId", this.existingDropdown.value);
    existingFormData.append("itemNumber", this.product.itemNumber);
    existingFormData.append("qty", this.product.quantity);

    return existingFormData;
  }

  makeNotification() {
    const list = this.existingDropdown.querySelector(":checked").textContent;

    new Notification({
      heading: `${this.product.quantity} item${
        this.product.quantity > 1 ? "s" : ""
      } added to your ${list}`,
      buttonText: "View Auto Reorder",
      href: `/myaccount:autoreorder/detail/?r=${this.existingDropdown.value}`,
      imageUrl: this.product.primaryImageSrc,
    });
  }

  async populateNextAutoReorderBanner() {
    const encodedItemNumber = encodeURIComponent(this.product.itemNumber);

    const response = await fetch(
      `/myaccount:autoreorder/getactiveautoreorderswithitemnumber/?itemNumber=${encodedItemNumber}`,
      {
        credentials: "include",
        headers: {
          accept: "application/json",
        },
      },
    );

    const autoReorders = await response.json();
    AutoReorder.renderNextAutoReorderBanner(autoReorders);
  }

  async addItemToExistingAutoReorder() {
    this.makeNotification();

    const response = await fetch(
      "/myaccount:autoreorder/additemtoexistingautoreorder/",
      {
        body: this.existingFormData(),
        method: "POST",
        credentials: "include",
        headers: {
          accept: "application/json",
        },
      },
    );

    const populateModal = await response.json();

    if (populateModal.isChangedToCommonCarrier) {
      document.getElementById("autoReorderIdModal").innerHTML =
        this.existingDropdown.value;

      const lgYesNoSwitchModal = document.getElementById("lgYesNoSwitchModal");
      const cbdYesNoSwitchModal = document.getElementById(
        "cbdYesNoSwitchModal",
      );

      lgYesNoSwitchModal.disabled = false;
      lgYesNoSwitchModal.value = "";

      if (populateModal.hasResidential) {
        cbdYesNoSwitchModal.disabled = true;
        cbdYesNoSwitchModal.value = 1;
      } else {
        cbdYesNoSwitchModal.disabled = false;
        cbdYesNoSwitchModal.value = "";
      }

      if (populateModal.customerLiftgateFee === 0) {
        document.getElementById("customerLiftgateFee").innerHTML = "";
      } else {
        document.getElementById("customerLiftgateFee").innerHTML =
          `($${populateModal.customerLiftgateFee} fee)`;
      }

      this.commonCarrierShippingOptions.show();
    }

    this.populateNextAutoReorderBanner();
  }

  addEventListenerToCommonCarrierSettingsModalSaveButton() {
    const ccSettingsSaveButton = document.getElementById(
      "commonCarrierModalSave",
    );
    const externalSubmitButton = document.getElementById("buyButton");

    ccSettingsSaveButton.addEventListener("click", async () => {
      const lgYesNoSwitchModal = document.getElementById("lgYesNoSwitchModal");
      const cbdYesNoSwitchModal = document.getElementById(
        "cbdYesNoSwitchModal",
      );

      if (!lgYesNoSwitchModal.value || !cbdYesNoSwitchModal.value) {
        alert("You must pick selections.");
      } else {
        const body = new FormData();
        body.append(
          "autoReorderId",
          document.getElementById("autoReorderIdModal").innerHTML,
        );
        body.append("liftGate", lgYesNoSwitchModal.value);
        body.append("callBeforeDelivery", cbdYesNoSwitchModal.value);

        externalSubmitButton.disabled = true;
        ccSettingsSaveButton.disabled = true;

        const response = await fetch(
          "/myaccount:autoreorder/savecommoncarriersettings/",
          {
            body,
            method: "POST",
            credentials: "include",
            headers: {
              accept: "application/json",
            },
          },
        );

        if (response.ok) {
          alert("Saved common carrier settings");
        } else {
          alert("Error saving common carrier settings");
        }

        externalSubmitButton.disabled = false;
        ccSettingsSaveButton.disabled = false;

        this.commonCarrierShippingOptions.hide();

        lgYesNoSwitchModal.value = "";
        cbdYesNoSwitchModal.value = "";
      }
    });
  }
}
